import React from 'react';
import MiniTwrChart from './MiniTwrChart';
import AllocationBlock from './AllocationBlock';
import { fmt$, signed$, fmtPct, fmtDate, pnlClr } from './fmt';

// One account's performance surface — the render body that WAS AutomatedTrading before the
// list→detail restructure, lifted out verbatim so it renders identically for a single account:
// a row of KPI tiles, an equity-curve chart with the benchmark (SPX) overlay, and the
// allocation + holdings panel. It takes ONE account view-model as a prop and branches on
// nothing about where the numbers came from — the adapter already reconciled that.
//
// The Illustrative banner is defined AND exported here because both surfaces need the identical
// mark: the list shows it above the account rows, the detail shows it above the tiles, and the
// wording/prominence must not drift between them — so there is one component, gated the same way
// (shown only when the account is not real).

// Sticky "not real figures" warning, matching the AUM Dashboard's Illustrative banner in
// wording and prominence: solid, high-contrast, not dismissible, and pinned so it does not
// scroll away and leave authoritative-looking numbers with nothing qualifying them.
export const Illustrative = () => (
  <div className="sticky top-0 z-30 mb-4 rounded-xl border-2 border-amber-500 bg-amber-100 px-4 py-3 shadow-sm dark:border-amber-500/70 dark:bg-amber-950/80 dark:backdrop-blur">
    <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1">
      <span className="rounded bg-amber-500 px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.18em] text-white">
        Illustrative
      </span>
      <span className="text-[14px] font-bold text-amber-950 dark:text-amber-100">
        These are not real figures.
      </span>
    </div>
    <p className="mt-1.5 text-[12px] leading-relaxed text-amber-900 dark:text-amber-200/90">
      Nothing on this screen is a real holding, valuation, return or trade. The layout is being
      specified; no live trading data is connected. Do not use, quote or circulate any number
      from this screen.
    </p>
  </div>
);

// A KPI tile in GreenSkew's register: uppercase label, serif tabular figure, a supporting
// subtitle. `valueClass` carries the sign colour (P&L) or the brand accent (Sharpe).
const Kpi = ({ label, value, valueClass = 'text-gray-900 dark:text-gray-100', sub }) => (
  <div className="rounded-xl border border-gray-200 bg-white px-3.5 py-3 dark:border-gray-800 dark:bg-gray-900/40">
    <div className="text-[9.5px] font-medium uppercase leading-tight tracking-[0.15em] text-gray-400">{label}</div>
    <div className={`mt-1.5 font-serif text-[19px] leading-none tabular-nums sm:text-2xl ${valueClass}`}>{value}</div>
    {sub && <div className="mt-1.5 text-[10.5px] leading-tight text-gray-400">{sub}</div>}
  </div>
);

// Last non-null value of a rebased series — the full-window return for the chart header. The
// benchmark can end null (a gap at the window edge), so the last defined point is used, not
// blindly the last element.
const lastVal = (curve) => {
  for (let i = (curve || []).length - 1; i >= 0; i -= 1) {
    if (curve[i] && curve[i].value != null) return curve[i].value;
  }
  return null;
};

export default function AccountDetail({ account }) {
  const data = account;

  // Full-window returns for the chart header — the last defined point of each rebased series.
  const portReturn = lastVal(data.equityCurve);
  const spxReturn = lastVal(data.benchmarkCurve);

  return (
    <div>
      {/* The banner renders ONLY when the account is not real. When live figures are showing it
          is hidden — the account name + "As at" dateline below are what qualify them instead. */}
      {!data.isReal && <Illustrative />}

      <div className="mb-4">
        <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
          <h2 className="font-serif text-2xl text-gray-900 dark:text-gray-100">{data.displayName}</h2>
          {data.asAt && <span className="text-[11px] text-gray-400">As at {fmtDate(data.asAt)}</span>}
        </div>
        {/* What the account IS (source-sent description) and when it started. Both optional. */}
        {data.description && (
          <p className="mt-1.5 max-w-2xl text-[12.5px] leading-relaxed text-gray-500 dark:text-gray-400">
            {data.description}
          </p>
        )}
        {data.inceptionDate && (
          <p className="mt-1 text-[10.5px] uppercase tracking-wide text-gray-400">
            Since {fmtDate(data.inceptionDate)}
          </p>
        )}
      </div>

      {/* ── KPI tiles ──
          The tile SET depends on the wire shape (the adapter sets isNewShape):

          NEW (levered) shape — EXACTLY FOUR tiles, in this order, that reconcile
          NAV = Gross positions + Cash:
            NAV             ← aum field       (equity = infused + P&L)
            Gross positions ← gross_positions (the deployed book)
            PNL             ← total_pnl       (signed, green/red)
            Cash            ← cash field      (NET FINANCING — negative under leverage, shown as
                                               "Cash" in red; it is the financing that bridges the
                                               deployed book down to equity, NOT spare cash.
                                               Deliberately shown, not hidden or renamed.)
          Sharpe + volatility are NOT tiles in this shape — they sit as a caption by the chart.
          We do NOT render the reconciliation total; the four tiles carry the identity themselves.

          OLD (live today) shape — the current AUM / Cash / Total P&L / Sharpe tiles, unchanged,
          where AUM is the deployed book and Cash is POSITIVE spare cash. */}
      {data.isNewShape ? (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5 sm:gap-3">
          <Kpi label="NAV" value={fmt$(data.nav)} sub="equity · infused + P&L" />
          <Kpi label="Gross positions" value={fmt$(data.grossPositions)} sub="deployed book" />
          <Kpi label="PNL" value={signed$(data.totalPnl)} valueClass={pnlClr(data.totalPnl)} sub="since inception" />
          <Kpi label="Cash" value={fmt$(data.cash)} valueClass={pnlClr(data.cash)}
               sub={data.cash != null && data.cash < 0 ? 'net financing · borrowed' : 'net financing'} />
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5 sm:gap-3">
          <Kpi label="AUM" value={fmt$(data.aum)} sub="invested + cash" />
          <Kpi label="Cash" value={fmt$(data.cash)} sub={data.cashPct == null ? 'uninvested' : `${data.cashPct.toFixed(1)}% of AUM`} />
          <Kpi label="Total P&L" value={signed$(data.totalPnl)} valueClass={pnlClr(data.totalPnl)} sub="since inception" />
          <Kpi label="Sharpe" value={data.sharpe == null ? '—' : data.sharpe.toFixed(2)} valueClass="text-brand-gold"
               sub={data.annVol == null ? 'since inception' : `vol ${(data.annVol * 100).toFixed(1)}% · since inception`} />
        </div>
      )}

      {/* ── equity-curve hero: portfolio area + dashed SPX overlay ── */}
      <div className="mt-4 bg-white dark:bg-[#161b22] border border-gray-200 dark:border-gray-800 rounded-xl p-4">
        <div className="flex flex-wrap items-center gap-x-6 gap-y-2 mb-3">
          <div className="flex items-baseline gap-2">
            <span className="inline-block w-3 h-[3px] rounded self-center" style={{ background: 'rgb(var(--brand-gold-rgb))' }} />
            <span className="text-xs text-gray-500">Portfolio</span>
            <span className={`font-mono text-[20px] tabular-nums ${pnlClr(portReturn)}`}>{fmtPct(portReturn == null ? null : portReturn * 100, 2)}</span>
          </div>
          <div className="flex items-baseline gap-2">
            <span className="inline-block w-3 h-[3px] rounded self-center" style={{ background: '#d4696f' }} />
            <span className="text-xs text-gray-500">{data.benchmarkLabel || 'SPX'}</span>
            <span className={`font-mono text-[20px] tabular-nums ${pnlClr(spxReturn)}`}>{fmtPct(spxReturn == null ? null : spxReturn * 100, 2)}</span>
          </div>
          <span className="ml-auto text-[10.5px] uppercase tracking-wide text-gray-400">Since inception</span>
        </div>
        <MiniTwrChart points={data.equityCurve} spyPoints={data.benchmarkCurve} />
        {/* In the NEW shape Sharpe + vol are not tiles, so they live here as a caption so the
            risk figures are not lost. In the OLD shape the Sharpe tile carries them, so the left
            side stays empty and "Hover to scrub" keeps its right-aligned position. */}
        <div className="flex items-baseline justify-between mt-2 text-[10.5px] text-gray-400">
          {data.isNewShape ? (
            <span>
              Sharpe {data.sharpe == null ? '—' : data.sharpe.toFixed(2)}
              {data.annVol == null ? '' : ` · vol ${(data.annVol * 100).toFixed(1)}%`}
            </span>
          ) : <span aria-hidden="true" />}
          <span>Hover to scrub</span>
        </div>
      </div>

      {/* ── allocation & holdings — Security · Bucket · Sector, table + donut ── */}
      <div className="mt-4">
        <AllocationBlock holdings={data.holdings} bySector={data.bySector}
                         byBucket={data.byBucket} bookPnl={data.bookPnl} />
      </div>
    </div>
  );
}
